import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, requestErrorMessage, waLink, mailtoLink } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Search, Users, Eye, Pencil, Trash2, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", phone: "", email: "", gstin: "", pan: "", billing_address: "",
  shipping_address: "", state: "Telangana", state_code: "36", credit_limit: 0,
  payment_terms: "Net 30", opening_balance: 0, customer_type: "Medical Shop",
};

const STATES = [
  ["36", "Telangana"], ["37", "Andhra Pradesh"], ["29", "Karnataka"], ["27", "Maharashtra"],
  ["33", "Tamil Nadu"], ["07", "Delhi"], ["24", "Gujarat"], ["09", "Uttar Pradesh"],
  ["19", "West Bengal"], ["32", "Kerala"],
];

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const r = await http.get("/customers", { params: { search } }); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, "load customers")); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required");
    try {
      if (editId) await http.put(`/customers/${editId}`, form);
      else await http.post("/customers", form);
      toast.success(editId ? "Customer updated" : "Customer added");
      setOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, editId ? "update customer" : "add customer")); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try { await http.delete(`/customers/${id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete customer")); }
  };

  const setState = (code) => {
    const name = STATES.find((s) => s[0] === code)?.[1] || "";
    setForm({ ...form, state_code: code, state: name });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground">{rows.length} parties</p>
        </div>
        <Button onClick={openNew} data-testid="add-customer-button"><Plus className="h-4 w-4 mr-1" /> Add Customer</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loadError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name, phone, GSTIN…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="customer-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">ID</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Phone</th>
                <th className="px-4 py-3 font-medium">GSTIN</th>
                <th className="px-4 py-3 font-medium">State</th>
                <th className="px-4 py-3 font-medium text-right">Credit Limit</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-brand-600" data-testid={`customer-id-${c.id}`}>{c.party_no || "—"}</td>
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.customer_type}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.gstin || "—"}</td>
                  <td className="px-4 py-3">{c.state}</td>
                  <td className="px-4 py-3 text-right font-mono">{inr(c.credit_limit)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { if (!c.phone) return toast.error("No phone saved"); window.open(waLink(c.phone, `Dear ${c.name},`), "_blank"); }} className="p-1.5 hover:bg-emerald-50 rounded" title="WhatsApp" data-testid={`wa-customer-${c.id}`}><MessageCircle className="h-4 w-4 text-emerald-600" /></button>
                      <button onClick={() => { if (!c.email) return toast.error("No email saved"); window.location.href = mailtoLink(c.email, `Hello ${c.name}`, ""); }} className="p-1.5 hover:bg-slate-100 rounded" title="Email" data-testid={`email-customer-${c.id}`}><Mail className="h-4 w-4 text-brand-600" /></button>
                      <button onClick={() => navigate(`/parties/customers/${c.id}`)} className="p-1.5 hover:bg-slate-100 rounded" title="Ledger" data-testid={`view-customer-${c.id}`}><Eye className="h-4 w-4 text-slate-600" /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit" data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                      <button onClick={() => del(c.id)} className="p-1.5 hover:bg-rose-50 rounded" title="Delete" data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loadError && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />No customers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Customer" : "Add Customer"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="customer-name-input" />
            </div>
            <div>
              <Label>Customer Type</Label>
              <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Medical Shop", "Distributor", "Hospital", "Doctor", "C&F", "Retailer"].map((t) =>
                    <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="customer-phone-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="mt-1.5 font-mono" data-testid="customer-gstin-input" /></div>
            <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="mt-1.5 font-mono" /></div>
            <div>
              <Label>State (Place of Supply)</Label>
              <Select value={form.state_code} onValueChange={setState}>
                <SelectTrigger className="mt-1.5" data-testid="customer-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s[0]} value={s[0]}>{s[0]} — {s[1]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Billing Address</Label><Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>Shipping Address</Label><Input value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Credit Limit</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: +e.target.value })} className="mt-1.5" /></div>
            <div><Label>Opening Balance</Label><Input type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: +e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-customer-button">Save Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
